import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { Session } from '../types';

const SessionsPage: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/v1/sessions');
      setSessions(response.data.data?.sessions || []);
    } catch (err: any) {
      console.error('Error fetching sessions:', err);

      // Only set error for actual failures, not empty results
      if (err.response) {
        // Server responded with error status (4xx, 5xx)
        setError(err.response.data?.error?.message || 'Failed to load sessions');
      } else if (err.request) {
        // Network error - no response received
        setError('Network error. Please check your connection.');
      } else {
        // Other errors
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: 'bg-secondary',
      confirmed: 'bg-success',
      rejected: 'bg-danger'
    };
    return badges[status as keyof typeof badges] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-calendar-alt me-2"></i>
          My Sessions
        </h2>
        <Link to="/sessions/new" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>
          Create Session
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
            <h4>No Sessions Yet</h4>
            <p className="text-muted">
              Create your first session to propose talks at events.
            </p>
            <Link to="/sessions/new" className="btn btn-primary">
              <i className="fas fa-plus me-2"></i>
              Create Your First Session
            </Link>
          </div>
        </div>
      ) : (
        <div className="row">
          {sessions.map(session => (
            <div key={session.session_id} className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="card-title mb-0">{session.talk_title || 'Untitled Talk'}</h5>
                    <span className={`badge ${getStatusBadge(session.status)}`}>
                      {session.status}
                    </span>
                  </div>
                  <p className="text-muted mb-2">
                    <i className="fas fa-calendar me-1"></i>
                    {session.event_name || 'Unknown Event'}
                  </p>
                  {session.start_time && (
                    <p className="text-muted mb-2">
                      <i className="fas fa-clock me-1"></i>
                      {new Date(session.start_time).toLocaleString()}
                    </p>
                  )}
                  {session.room_or_track && (
                    <p className="text-muted mb-2">
                      <i className="fas fa-map-marker-alt me-1"></i>
                      {session.room_or_track}
                    </p>
                  )}
                  <div className="text-muted small">
                    <i className="fas fa-calendar me-1"></i>
                    Created {new Date(session.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="card-footer bg-white border-top-0">
                  <Link
                    to={`/sessions/${session.session_id}`}
                    className="btn btn-sm btn-outline-primary"
                  >
                    <i className="fas fa-eye me-1"></i>
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionsPage;
